<template>
  <v-container fluid class="pa-0">
    <!-- Vídeo de fundo -->
    <div class="video-container position-relative">
      <video
        autoplay
        muted
        loop
        playsinline
        class="background-video"
      >
        <source :src="backgroundVideo" type="video/mp4" />
      </video>
    </div>

    <!-- Botão Download -->
    <div class="download-button-wrapper">
      <v-btn
        color="#5c3e62"
        class="pulse"
        href="/download"
        target="_blank"
        rel="noopener"
      >
        <v-icon left>mdi-download</v-icon>
        {{ t('menu.download') }}
      </v-btn>
    </div>

    <!-- Seção de Rankings -->
    <div class="ranking-videos">
      <div
        v-for="rank in rankSummaries"
        :key="rank.key"
        class="ranking-item"
      >
        <VTooltip
          open-on-hover
          location="top"
        >
          <template #activator="{ props }">
            <div v-bind="props" class="flip-container">
              <div class="flipper">
                <img
                  class="front ranking-image"
                  :src="iconezephyr"
                  alt="Ranking Icon"
                />
                <img
                  class="back ranking-image"
                  :src="iconezephyrcoroa"
                  alt="Coroa Icon"
                />
              </div>
            </div>
          </template>
          <span>{{ t(rank.label) }}</span>
        </VTooltip>

        <div class="rank-player">
          {{ rank.top ? rank.top.player_name : t('ranks.noData') }}
        </div>
      </div>
    </div>
  </v-container>
</template>

<script setup>
import { reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import backgroundVideo from '@/assets/background.mp4'
import iconezephyr from '@/assets/rank/iconezephyr.png'
import iconezephyrcoroa from '@/assets/rank/iconezephyrcoroa.png'
import {
  fetchRankDeaths,
  fetchRankCrystalDamage,
  fetchRankHeal,
  fetchRankPlayerDamage,
  fetchRankWins
} from '@/services/api'

// Função de tradução
const { t } = useI18n({ useScope: 'global' })

// Configuração dos 5 rankings
const rankSummaries = reactive([
  { key: 'deaths',         label: 'ranks.deaths',       fn: fetchRankDeaths,        top: null },
  { key: 'crystalDamage',  label: 'ranks.crystalDamage', fn: fetchRankCrystalDamage, top: null },
  { key: 'heal',           label: 'ranks.heal',         fn: fetchRankHeal,          top: null },
  { key: 'playerDamage',   label: 'ranks.playerDamage', fn: fetchRankPlayerDamage,  top: null },
  { key: 'wins',           label: 'ranks.wins',         fn: fetchRankWins,          top: null },
])

// Ao montar, busca cada ranking passando o token
onMounted(async () => {
  const token = localStorage.getItem('token')
  for (const summary of rankSummaries) {
    try {
      const rows = await summary.fn(token)
      summary.top = rows.length ? rows[0] : null
    } catch (err) {
      console.error(`Erro ao buscar ${summary.key}`, err)
      summary.top = null
    }
  }
})
</script>

<style scoped>
/* Corpo e overflow */
html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: black;
}

.v-container.fluid.pa-0 {
  padding: 0 !important;
  margin: 0 !important;
  min-height: 100vh;
  width: 100vw;
  overflow: hidden;
}

/* Vídeo de fundo ocupa toda a tela */
.video-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: black;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}
.background-video {
  width: 100vw;
  height: auto;
  object-fit: contain;
  object-position: top;
  display: block;
}

/* Botão download */
.download-button-wrapper {
  position: absolute;
  top: 85px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}
.pulse {
  animation: pulse-animation 1s infinite;
}
@keyframes pulse-animation {
  0%   { transform: scale(1);    box-shadow: 0 0 0 0 rgba(192,57,43,0.7); }
  70%  { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(192,57,43,0); }
  100% { transform: scale(1);    box-shadow: 0 0 0 0 rgba(192,57,43,0); }
}

/* Seção de Rankings */
.ranking-videos {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  background: linear-gradient(
    to right,
    #000,
    #2a004f,
    #4b0082,
    #240cc4
  );
  padding: 12px 0;
  width: 100%;
  position: relative;
}

/* Cada item de ranking: maior altura para comportar imagem e nome */
.ranking-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100px;
  min-height: 140px;
  padding-top: 8px;
}

/* Flip container e animação de moeda */
.flip-container {
  perspective: 600px;
  margin-bottom: 12px;
}
.flipper {
  position: relative;
  width: 80px;
  height: 80px;
  transform-style: preserve-3d;
  transition: transform 0.6s;
}
.flip-container:hover .flipper {
  transform: rotateY(180deg);
}
.flipper .front,
.flipper .back {
  backface-visibility: hidden;
  position: absolute;
  top: 0;
  left: 0;
  width: 80px;
  height: 80px;
}
.flipper .back {
  transform: rotateY(180deg);
}

/* Imagens do ranking */
.ranking-image {
  width: 80px;
  height: 80px;
  border-radius: 4px;
}

/* Nome do jogador */
.rank-player {
  text-align: center;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0,0,0,0.7);
  font-size: 0.875rem;
  font-weight: 700;
}

/* Evita barras de rolagem */
::-webkit-scrollbar {
  display: none;
}

/* === Ajustes para telas menores (responsivo) === */
@media (max-width: 768px) {
  /* Permitir scroll normal para agrupar vídeo + ranks */
  html, body {
    overflow: auto;
    height: auto;
  }
  .v-container.fluid.pa-0 {
    overflow: auto;
    min-height: auto;
  }

  /* Vídeo agora posiciona-se relativo e ajusta altura ao conteúdo */
  .video-container {
    position: relative;
    height: auto;
  }
  .background-video {
    width: 100%;
    height: auto;
    object-fit: cover;
    object-position: top;
  }

  /* Remove qualquer espaçamento extra acima dos ranks */
  .ranking-videos {
    margin-top: 0 !important;
    padding-top: 0;
    gap: 10px;
  }
}
</style>
